import React from 'react';
import SearchableAccordion, { AccordionCategory } from './SearchableAccordion';

const REFERENCE_DATA: AccordionCategory[] = [
  {
    id: 'core-concepts',
    title: 'Core Concepts',
    icon: 'ref-core-concepts',
    items: [
      {
        id: 'what-is-prompt-engineering',
        title: 'What is Prompt Engineering?',
        content: 'Prompt engineering is the practice of designing and refining inputs (prompts) to get desired outputs from AI language models. It involves crafting clear instructions, providing context, and structuring queries to maximize the quality and relevance of AI responses.',
      },
      {
        id: 'tokens',
        title: 'Tokens & Token Limits',
        content: 'Tokens are pieces of text that the AI processes - roughly 4 characters or 0.75 words per token. Models have token limits (e.g., 4096, 8192 tokens) that include both your prompt and the response. Longer prompts leave less room for responses.',
      },
      {
        id: 'temperature',
        title: 'Temperature',
        content: 'Temperature controls randomness in responses. Lower values (0.0-0.3) make output more focused and deterministic. Higher values (0.7-1.0) make output more creative and varied. Use low temperature for factual tasks, high for creative tasks.',
      },
      {
        id: 'top-k-top-p',
        title: 'Top-K & Top-P Sampling',
        content: 'Top-K limits the model to choosing from the K most likely next tokens. Top-P (nucleus sampling) chooses from the smallest set of tokens whose cumulative probability exceeds P. These control output diversity and quality.',
      },
      {
        id: 'context-window',
        title: 'Context Window',
        content: 'The context window is the maximum amount of text (in tokens) the model can consider at once. This includes your prompt, conversation history, and the response. Managing context is crucial for long conversations.',
      },
    ],
  },
  {
    id: 'techniques',
    title: 'Prompt Techniques',
    icon: 'ref-techniques',
    items: [
      {
        id: 'zero-shot',
        title: 'Zero-Shot Prompting',
        content: 'Asking the model to perform a task without providing examples. Works well for common tasks the model has seen during training.',
        examples: ['Translate this to French: Hello, how are you?', 'Summarize this article in 3 sentences.'],
      },
      {
        id: 'few-shot',
        title: 'Few-Shot Prompting',
        content: 'Providing 2-5 examples of the desired input-output pattern before asking the model to perform the task. This teaches the model your specific format and style.',
        examples: [
          'Example 1: Input: "Great product!" → Sentiment: Positive',
          'Example 2: Input: "Terrible service" → Sentiment: Negative',
          'Now classify: "Amazing experience!"',
        ],
      },
      {
        id: 'chain-of-thought',
        title: 'Chain-of-Thought (CoT)',
        content: 'Encouraging the model to show its reasoning process step-by-step. Add phrases like "Let\'s think step by step" or "Show your work" to improve accuracy on complex problems.',
        examples: ['Solve this problem step by step:', 'Let\'s break this down:', 'Think through this carefully:'],
      },
      {
        id: 'role-prompting',
        title: 'Role Prompting',
        content: 'Assigning a specific role or persona to the AI to shape its responses. This sets expectations for tone, expertise level, and perspective.',
        examples: [
          'You are a financial analyst with 10 years of experience...',
          'Act as a Python expert who explains concepts simply...',
          'You are a helpful customer service representative...',
        ],
      },
      {
        id: 'prompt-chaining',
        title: 'Prompt Chaining',
        content: 'Breaking complex tasks into multiple sequential prompts, where each prompt builds on the previous response. This improves accuracy for multi-step workflows.',
      },
      {
        id: 'self-consistency',
        title: 'Self-Consistency',
        content: 'Generating multiple responses to the same prompt and selecting the most consistent or common answer. Useful for improving reliability on reasoning tasks.',
      },
    ],
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    icon: 'ref-best-practices',
    items: [
      {
        id: 'be-specific',
        title: 'Be Specific and Clear',
        content: 'Vague prompts lead to vague responses. Specify exactly what you want: format, length, tone, audience, and constraints. The more precise your instructions, the better the output.',
      },
      {
        id: 'provide-context',
        title: 'Provide Context',
        content: 'Give the model relevant background information. Explain the scenario, audience, purpose, and any constraints. Context helps the model understand what kind of response is appropriate.',
      },
      {
        id: 'use-delimiters',
        title: 'Use Delimiters',
        content: 'Separate different parts of your prompt with clear markers like triple quotes ("""), dashes (---), or hashtags (###). This helps the model distinguish instructions from content.',
        examples: ['"""', '---', '###', '```'],
      },
      {
        id: 'specify-format',
        title: 'Specify Output Format',
        content: 'Tell the model exactly how to structure the response: bullet points, numbered lists, JSON, tables, paragraphs, etc. Include examples of the desired format.',
      },
      {
        id: 'break-down-tasks',
        title: 'Break Down Complex Tasks',
        content: 'Divide complicated requests into smaller, manageable steps. Either use multiple prompts or explicitly list the steps you want the model to follow.',
      },
      {
        id: 'iterate-refine',
        title: 'Iterate and Refine',
        content: 'Prompt engineering is iterative. Test your prompts, analyze the results, and refine based on what works. Small changes can significantly improve output quality.',
      },
    ],
  },
  {
    id: 'patterns',
    title: 'Common Patterns',
    icon: 'ref-patterns',
    items: [
      {
        id: 'analysis-template',
        title: 'Analysis Template',
        content: 'Structure: Context → Question → Requirements → Output Format',
        examples: [
          'Context: [Provide background]',
          'Analyze: [Specific question]',
          'Consider: [Key factors]',
          'Format: [Desired structure]',
        ],
      },
      {
        id: 'summarization-template',
        title: 'Summarization Template',
        content: 'Specify length, key points to include, and audience level.',
        examples: [
          'Summarize the following in 3 bullet points:',
          'Create an executive summary (200 words):',
          'Extract the main takeaways:',
        ],
      },
      {
        id: 'classification-template',
        title: 'Classification Template',
        content: 'Define categories, provide examples, and specify output format.',
        examples: [
          'Classify into: [Category A, Category B, Category C]',
          'Based on these examples: [examples]',
          'Output format: Category name only',
        ],
      },
      {
        id: 'creative-template',
        title: 'Creative Writing Template',
        content: 'Set tone, style, constraints, and target audience.',
        examples: [
          'Write in a [tone] style',
          'Target audience: [audience]',
          'Length: [word count]',
          'Include: [specific elements]',
        ],
      },
    ],
  },
];

const Reference: React.FC = () => (
  <SearchableAccordion
    heading="Reference"
    subheading="Your guide to prompt engineering concepts, techniques, and best practices."
    searchPlaceholder="Search for topics, techniques, or terms…"
    categories={REFERENCE_DATA}
    defaultExpandedCategory="core-concepts"
  />
);

export default Reference;
